package com.evoapps.evoshift.ui.finance

import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.TrendingDown
import androidx.compose.material.icons.automirrored.filled.TrendingFlat
import androidx.compose.material.icons.automirrored.filled.TrendingUp
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.comparison.Change
import com.evoapps.evoshift.domain.comparison.ComparisonMode
import com.evoapps.evoshift.widgets.LocalOnColours
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.comparison_metric_sales
import evoshift.composeapp.generated.resources.comparison_mode_last_week
import evoshift.composeapp.generated.resources.comparison_mode_last_year_date
import evoshift.composeapp.generated.resources.comparison_mode_last_year_weekday
import evoshift.composeapp.generated.resources.comparison_no_last_week
import evoshift.composeapp.generated.resources.comparison_no_last_year
import evoshift.composeapp.generated.resources.comparison_spoken_down
import evoshift.composeapp.generated.resources.comparison_spoken_last_week
import evoshift.composeapp.generated.resources.comparison_spoken_last_year
import evoshift.composeapp.generated.resources.comparison_spoken_no_data
import evoshift.composeapp.generated.resources.comparison_spoken_same
import evoshift.composeapp.generated.resources.comparison_spoken_up
import evoshift.composeapp.generated.resources.comparison_vs_last_week
import evoshift.composeapp.generated.resources.comparison_vs_last_year
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import kotlin.math.abs

/*
 * SH-225: the sales comparison chips ("vs zeszły tydzień +12%", "vs rok temu −3%") on the owner's
 * dashboard and the Money screen. Every change carries its sign (never colour alone) and is spelt
 * out for screen readers ("w porównaniu z zeszłym tygodniem: wzrost o 12%"). A side without
 * takings is "no data", never 0. SH-228: the charts moved from the daily results to the
 * Statistics screen (StatisticsScreen.kt).
 */

/** Test tag of the chips (the dashboard's and the Money screen's). */
const val COMPARISON_CHIP_TAG = "comparison_chip"

private const val MINUS = "−"

/** "+12,3%", "−3%", "0%": the sign always shown. */
fun formatSignedPercent(value: Double): String = signOf(value) + formatPercent(abs(value))

/** "+400,00 zł", "−1 250,00 zł". */
fun formatSignedMoney(value: Double, currency: String): String = signOf(value) + formatMoney(abs(value), currency)

internal fun signOf(value: Double): String = when {
    // As the figure shows it: a change that rounds to 0 has no sign.
    abs(value) < 0.05 -> ""
    value > 0 -> "+"
    else -> MINUS
}

fun comparisonModeRes(mode: ComparisonMode): StringResource = when (mode) {
    ComparisonMode.LAST_WEEK -> Res.string.comparison_mode_last_week
    ComparisonMode.LAST_YEAR_WEEKDAY -> Res.string.comparison_mode_last_year_weekday
    ComparisonMode.LAST_YEAR_DATE -> Res.string.comparison_mode_last_year_date
}

/** The short "vs zeszły tydzień" of a chip. */
private fun chipPrefixRes(mode: ComparisonMode) = if (mode.isLastYear) Res.string.comparison_vs_last_year else Res.string.comparison_vs_last_week

/** The spelt-out "w porównaniu z zeszłym tygodniem" for screen readers. */
private fun spokenRes(mode: ComparisonMode) = if (mode.isLastYear) Res.string.comparison_spoken_last_year else Res.string.comparison_spoken_last_week

private fun noDataRes(mode: ComparisonMode) = if (mode.isLastYear) Res.string.comparison_no_last_year else Res.string.comparison_no_last_week

/** "Sprzedaż netto w porównaniu z zeszłym tygodniem: wzrost o 12,3%" (or no change, or no data). */
@Composable
fun spokenChange(metric: String, mode: ComparisonMode, percent: Double?): String {
    val against = stringResource(spokenRes(mode))
    return when {
        percent == null -> stringResource(Res.string.comparison_spoken_no_data, metric, against)
        abs(percent) < 0.05 -> stringResource(Res.string.comparison_spoken_same, metric, against)
        percent > 0 -> stringResource(Res.string.comparison_spoken_up, metric, against, formatPercent(percent))
        else -> stringResource(Res.string.comparison_spoken_down, metric, against, formatPercent(-percent))
    }
}

/**
 * A small chip "vs zeszły tydzień +12,3%": green-ish up, red-ish down for sales ([goodWhenUp]),
 * the sign and an arrow always there; without a % (no data, or a base of 0) "Brak danych z
 * zeszłego tygodnia" in the secondary colour.
 */
@Composable
fun ComparisonChip(
    mode: ComparisonMode,
    change: Change,
    modifier: Modifier = Modifier,
    metric: String = stringResource(Res.string.comparison_metric_sales),
    goodWhenUp: Boolean = true,
    compact: Boolean = false,
    /** SH-226/SH-227: what the two days were like ("rain (a year ago: sunny)"), after the change. */
    note: String? = null
) {
    val colours = LocalOnColours.current
    val percent = change.percent
    val colour = when {
        percent == null || abs(percent) < 0.05 -> colours.onSurfaceVariant
        (percent > 0) == goodWhenUp -> colours.success
        else -> colours.error
    }
    val text = (if (percent == null) stringResource(noDataRes(mode)) else "${stringResource(chipPrefixRes(mode))} ${formatSignedPercent(percent)}") +
        (note?.let { ", $it" } ?: "")
    val spoken = spokenChange(metric, mode, percent) + (note?.let { ", $it" } ?: "")
    Row(
        modifier
            // One node for screen readers, the change spelt out ("wzrost o 12%"), not the glyphs.
            .clearAndSetSemantics {
                contentDescription = spoken
                testTag = COMPARISON_CHIP_TAG
            }
            .border(1.dp, colour.copy(alpha = 0.5f), RoundedCornerShape(8.dp))
            .padding(horizontal = 6.dp, vertical = 2.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (percent != null) {
            Icon(
                when {
                    abs(percent) < 0.05 -> Icons.AutoMirrored.Filled.TrendingFlat
                    percent > 0 -> Icons.AutoMirrored.Filled.TrendingUp
                    else -> Icons.AutoMirrored.Filled.TrendingDown
                },
                contentDescription = null, tint = colour, modifier = Modifier.size(14.dp)
            )
            Spacer(Modifier.width(4.dp))
        }
        Text(
            text, style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold, color = colour,
            maxLines = if (compact || note != null) 2 else 1, overflow = TextOverflow.Ellipsis, textAlign = if (compact) TextAlign.End else TextAlign.Start
        )
    }
}
